import type { Connection } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import type { CustomNode, NodeStruct } from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import {
  getBranchLabelFromHandle,
  getConditionalBranchLabel,
  getControlTargetHandleId,
  getSwitchBranchLabels,
  getSwitchConfig,
  isBranchingStruct,
  isConditionalEdge,
  SWITCH_NODE_ID,
  type JsonValue,
} from './functional-control-flow';
import { declaredCalculateSample } from './calculate';

/**
 * Keeps a Switch node's conditional edges in step with its configuration.
 *
 * Contract (shared with the backend's branch gating and assembler v2):
 * a conditional edge is `edge_type: "conditional"` from the Switch to the
 * target node, with the branch name in `data.branch_label`, no data binding
 * (`target_param_id`/`target_port_id` null) and the target's *control*
 * handle (`targetHandle === target node id`) as its landing point.
 */

export const isBranchHandle = (handleId: string | null | undefined): boolean =>
  getBranchLabelFromHandle(handleId) !== null;

export const isBranchConnection = (
  connection: Pick<Connection, 'sourceHandle'>,
): boolean => isBranchHandle(connection.sourceHandle);

export const isSwitchStruct = (struct: NodeStruct | undefined): boolean =>
  struct?.node_id === SWITCH_NODE_ID;

// --- if/else (Phase 8b): the same edge sync serves an If's two branches ---
export const isBranchingSourceStruct = (
  struct: NodeStruct | undefined,
): boolean => isBranchingStruct(struct);
// --- end if/else ---

/** The Switch's fixed `selected_case` output port id, when present. */
export const getSelectedCasePortId = (struct: NodeStruct): string | null =>
  struct.output_ports?.find((port) => port.name === 'selected_case')?.id ??
  null;

export const buildConditionalEdge = (params: {
  connection: Connection;
  sourceNode: CustomNode;
  targetNode: CustomNode;
  branchLabel: string;
  layoutType: string;
}): CustomEdge => {
  const { connection, sourceNode, targetNode, branchLabel, layoutType } =
    params;
  return {
    id: uuidv4(),
    source: connection.source,
    sourceHandle: connection.sourceHandle ?? '',
    target: connection.target,
    targetHandle: getControlTargetHandleId(connection.target),
    sourceNodeId: sourceNode.data.struct.react_flow_generated_id,
    targetNodeId: targetNode.data.struct.react_flow_generated_id,
    source_node_id: connection.source,
    source_port_id:
      getSelectedCasePortId(sourceNode.data.struct) ??
      connection.sourceHandle ??
      null,
    target_node_id: connection.target,
    target_port_id: null,
    target_param_id: null,
    type: layoutType,
    edge_type: ConnectionType.CONDITIONAL,
    animated: false,
    data: { branch_label: branchLabel },
  };
};

export const edgesForBranch = (
  edges: readonly CustomEdge[],
  switchNodeId: string,
  label: string,
): CustomEdge[] =>
  edges.filter(
    (edge) =>
      edge.source === switchNodeId &&
      isConditionalEdge(edge) &&
      getConditionalBranchLabel(edge) === label,
  );

/** Rename a branch on every edge that carries it (label and handle). */
export const renameBranchLabel = (
  edges: readonly CustomEdge[],
  switchNodeId: string,
  oldLabel: string,
  newLabel: string,
): CustomEdge[] => {
  if (oldLabel === newLabel) return [...edges];
  const oldHandle = `control-branch:${encodeURIComponent(oldLabel)}`;
  const newHandle = `control-branch:${encodeURIComponent(newLabel)}`;
  return edges.map((edge) => {
    if (
      edge.source !== switchNodeId ||
      !isConditionalEdge(edge) ||
      getConditionalBranchLabel(edge) !== oldLabel
    ) {
      return edge;
    }
    return {
      ...edge,
      sourceHandle:
        edge.sourceHandle === oldHandle ? newHandle : edge.sourceHandle,
      data: { ...(edge.data ?? {}), branch_label: newLabel },
    };
  });
};

/** Branch labels on the Switch that have no conditional edge yet. */
export const unconnectedBranchLabels = (
  struct: NodeStruct,
  edges: readonly CustomEdge[],
  switchNodeId: string,
): string[] =>
  getSwitchBranchLabels(struct).filter(
    (label) => edgesForBranch(edges, switchNodeId, label).length === 0,
  );

/** Conditional edges whose label no longer exists on the Switch. */
export const orphanedBranchEdges = (
  struct: NodeStruct,
  edges: readonly CustomEdge[],
  switchNodeId: string,
): CustomEdge[] => {
  const labels = new Set(getSwitchBranchLabels(struct));
  return edges.filter(
    (edge) =>
      edge.source === switchNodeId &&
      isConditionalEdge(edge) &&
      !labels.has(getConditionalBranchLabel(edge) ?? ''),
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Unwrap a `NodeOutput` envelope (`{_node_output: true, data, ...}`). */
const unwrapEnvelope = (value: unknown): unknown =>
  isRecord(value) && value._node_output === true ? value.data : value;

/**
 * The last value that reached this Switch's `value` input, read from the
 * upstream node's stored run result. `undefined` when there is no run yet.
 */
export const resolveSwitchInputSample = (
  switchNode: CustomNode,
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
): JsonValue | undefined => {
  const valuePortId = switchNode.data.struct.params?.find(
    (param) => param.field_id === 'value',
  )?.bindable_config?.input_port?.id;
  if (!valuePortId) return undefined;
  const incoming = edges.find(
    (edge) =>
      edge.target === switchNode.id && edge.targetHandle === valuePortId,
  );
  if (!incoming) return undefined;
  const source = nodes.find((node) => node.id === incoming.source);
  const result = source?.data?.result as Record<string, unknown> | undefined;
  if (!isRecord(result)) {
    // A Calculate node declares its output names up front, so a downstream
    // picker can offer them before the first run (values unknown: null).
    return source?.data?.struct
      ? (declaredCalculateSample(source.data.struct) as JsonValue | undefined)
      : undefined;
  }
  if (result.status === 'skipped') return undefined;
  const portsData = isRecord(result.output_ports_data)
    ? result.output_ports_data
    : null;
  if (portsData && incoming.sourceHandle in portsData) {
    return unwrapEnvelope(portsData[incoming.sourceHandle]) as JsonValue;
  }
  if ('data' in result) return unwrapEnvelope(result.data) as JsonValue;
  return undefined;
};

/** Display names of the nodes a branch currently leads to. */
export const branchTargetNames = (
  edges: readonly CustomEdge[],
  nodes: readonly CustomNode[],
  switchNodeId: string,
  label: string,
): string[] =>
  edgesForBranch(edges, switchNodeId, label).map((edge) => {
    const target = nodes.find((node) => node.id === edge.target);
    const struct = target?.data?.struct;
    return (
      struct?.user_config_name ||
      (target?.data as { display_name?: string } | undefined)?.display_name ||
      struct?.display_name ||
      'a node'
    );
  });

export const switchBranchLabels = (
  struct: NodeStruct,
): {
  cases: string[];
  defaultLabel: string | null;
} => {
  const config = getSwitchConfig(struct);
  return {
    cases: config.cases.map((entry) => entry.label),
    defaultLabel: config.defaultLabel,
  };
};
